import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, Clock, MessageSquare, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const FORMSPREE_ENDPOINT = 'https://formspree.io/f/mojnlvad';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    nome: '',
    empresa: '',
    cargo: '',
    email: '',
    telefone: '',
    assunto: '',
    mensagem: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'E-mail é obrigatório';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'E-mail inválido';
    }

    if (!formData.telefone.trim()) {
      newErrors.telefone = 'Telefone é obrigatório';
    }

    if (!formData.assunto.trim()) {
      newErrors.assunto = 'Assunto é obrigatório';
    }

    if (!formData.mensagem.trim()) {
      newErrors.mensagem = 'Mensagem é obrigatória';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Por favor, corrija os erros no formulário.');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          nome: formData.nome,
          empresa: formData.empresa,
          cargo: formData.cargo,
          email: formData.email,
          telefone: formData.telefone,
          assunto: formData.assunto,
          mensagem: formData.mensagem
        })
      });

      if (response.ok) {
        toast.success('Mensagem enviada com sucesso! Entraremos em contato em breve.');
        setIsSubmitted(true);

        setFormData({
          nome: '',
          empresa: '',
          cargo: '',
          email: '',
          telefone: '',
          assunto: '',
          mensagem: ''
        });

        setTimeout(() => setIsSubmitted(false), 5000);
      } else {
        throw new Error('Erro ao enviar');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Erro ao enviar mensagem. Por favor, tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClasses = (fieldName) => `
    w-full px-4 py-3 rounded-xl border bg-white text-gray-900 
    placeholder-gray-400 transition-all duration-300
    focus:outline-none focus:ring-2 focus:ring-[#10b981] focus:border-transparent
    ${errors[fieldName] ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'}
  `;

  return (
    <>
      <Helmet>
        <title>Contato - DMM Security | Fale com Nossos Especialistas</title>
        <meta name="description" content="Entre em contato com a DMM Security. Agende uma conversa com nossos especialistas em segurança da informação, GRC e compliance." />
      </Helmet>

      <div className="min-h-screen pt-20" data-testid="contact-page">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-[#0f2535] via-[#1a3a52] to-[#1e4d6b] text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `linear-gradient(rgba(16,185,129,0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(16,185,129,0.2) 1px, transparent 1px)`,
              backgroundSize: '60px 60px'
            }} />
          </div>
          
          <div className="container mx-auto px-4 relative">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-4xl mx-auto text-center"
            >
              <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#10b981]/20 text-[#10b981] text-sm font-semibold rounded-full mb-6">
                <MessageSquare className="w-4 h-4" />
                Entre em Contato
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                Vamos Conversar?
              </h1>
              <p className="text-xl text-gray-300 leading-relaxed max-w-3xl mx-auto">
                Agende uma conversa com nossos especialistas e descubra como podemos elevar a maturidade de segurança da sua empresa
              </p>
            </motion.div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-20 bg-white" data-testid="contact-form-section">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
              {/* Contact Info */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-3xl font-bold text-[#1a3a52] mb-6">Informações de Contato</h2>
                <p className="text-gray-600 mb-8 leading-relaxed text-lg">
                  Estamos prontos para atender sua organização. Entre em contato através dos canais abaixo ou preencha o formulário.
                </p>

                <div className="space-y-6">
                  {/* Email */}
                  <a
                    href="mailto:contato@dmmsecurity.com.br"
                    className="flex items-start gap-4 p-4 rounded-xl bg-gray-50 hover:bg-[#10b981]/5 transition-colors group"
                    data-testid="contact-email"
                  >
                    <div className="w-12 h-12 bg-[#10b981]/10 group-hover:bg-[#10b981] rounded-xl flex items-center justify-center transition-colors">
                      <Mail className="w-6 h-6 text-[#10b981] group-hover:text-white transition-colors" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#1a3a52] mb-1">E-mail Corporativo</h3>
                      <span className="text-gray-600 group-hover:text-[#10b981] transition-colors">
                        contato@dmmsecurity.com.br
                      </span>
                    </div>
                  </a>

                  {/* Phone */}
                  <a
                    href="https://wa.me/5511987528279"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-4 p-4 rounded-xl bg-gray-50 hover:bg-[#10b981]/5 transition-colors group"
                    data-testid="contact-phone"
                  >
                    <div className="w-12 h-12 bg-[#10b981]/10 group-hover:bg-[#10b981] rounded-xl flex items-center justify-center transition-colors">
                      <Phone className="w-6 h-6 text-[#10b981] group-hover:text-white transition-colors" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#1a3a52] mb-1">Telefone / WhatsApp</h3>
                      <span className="text-gray-600 group-hover:text-[#10b981] transition-colors block">
                        (11) 4326-6707
                      </span>
                      <span className="text-gray-600 group-hover:text-[#10b981] transition-colors">
                        (11) 9 8752-8279
                      </span>
                    </div>
                  </a>

                  {/* Location */}
                  <div className="flex items-start gap-4 p-4 rounded-xl bg-gray-50">
                    <div className="w-12 h-12 bg-[#10b981]/10 rounded-xl flex items-center justify-center">
                      <MapPin className="w-6 h-6 text-[#10b981]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#1a3a52] mb-1">Localização</h3>
                      <span className="text-gray-600 block">Rua Canal de Suez, 357</span>
                      <span className="text-gray-600">Barueri / SP</span>
                    </div>
                  </div>

                  {/* Hours */}
                  <div className="flex items-start gap-4 p-4 rounded-xl bg-gray-50">
                    <div className="w-12 h-12 bg-[#10b981]/10 rounded-xl flex items-center justify-center">
                      <Clock className="w-6 h-6 text-[#10b981]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#1a3a52] mb-1">Horário de Atendimento</h3>
                      <span className="text-gray-600 block">Segunda a Sexta: 9h às 18h</span>
                      <span className="text-gray-500 text-sm">Sábados, Domingos e Feriados: Fechado</span>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Contact Form */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                {isSubmitted ? (
                  <div className="bg-gradient-to-br from-[#10b981]/10 to-emerald-50 rounded-2xl p-12 text-center border border-[#10b981]/20">
                    <div className="w-20 h-20 mx-auto mb-6 bg-[#10b981] rounded-full flex items-center justify-center">
                      <CheckCircle className="w-10 h-10 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-[#1a3a52] mb-4">Mensagem Enviada!</h3>
                    <p className="text-gray-600 mb-6">
                      Obrigado pelo seu contato. Nossa equipe entrará em contato em breve.
                    </p>
                    <button
                      onClick={() => setIsSubmitted(false)}
                      className="text-[#10b981] font-semibold hover:underline"
                    >
                      Enviar nova mensagem
                    </button>
                  </div>
                ) : (
                  <form 
                    onSubmit={handleSubmit} 
                    className="bg-gray-50 rounded-2xl p-8 shadow-sm border border-gray-100"
                    data-testid="contact-form"
                  >
                    <h2 className="text-2xl font-bold text-[#1a3a52] mb-6">Envie sua Mensagem</h2>

                    <div className="space-y-5">
                      {/* Nome */}
                      <div>
                        <label htmlFor="nome" className="block text-sm font-semibold text-[#1a3a52] mb-2">
                          Nome Completo *
                        </label>
                        <input
                          type="text"
                          id="nome"
                          name="nome"
                          value={formData.nome}
                          onChange={handleChange}
                          className={inputClasses('nome')}
                          placeholder="Seu nome completo"
                          data-testid="input-nome"
                        />
                        {errors.nome && <p className="text-red-500 text-sm mt-1">{errors.nome}</p>}
                      </div>

                      {/* Empresa & Cargo */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="empresa" className="block text-sm font-semibold text-[#1a3a52] mb-2">
                            Empresa
                          </label>
                          <input
                            type="text"
                            id="empresa"
                            name="empresa"
                            value={formData.empresa}
                            onChange={handleChange}
                            className={inputClasses('empresa')}
                            placeholder="Nome da empresa"
                            data-testid="input-empresa"
                          />
                        </div>

                        <div>
                          <label htmlFor="cargo" className="block text-sm font-semibold text-[#1a3a52] mb-2">
                            Cargo
                          </label>
                          <input
                            type="text"
                            id="cargo"
                            name="cargo"
                            value={formData.cargo}
                            onChange={handleChange}
                            className={inputClasses('cargo')}
                            placeholder="Seu cargo"
                            data-testid="input-cargo"
                          />
                        </div>
                      </div>

                      {/* Email */}
                      <div>
                        <label htmlFor="email" className="block text-sm font-semibold text-[#1a3a52] mb-2">
                          E-mail *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className={inputClasses('email')}
                          placeholder="seu@email.com.br"
                          data-testid="input-email"
                        />
                        {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                      </div>

                      {/* Telefone */}
                      <div>
                        <label htmlFor="telefone" className="block text-sm font-semibold text-[#1a3a52] mb-2">
                          Telefone *
                        </label>
                        <input
                          type="tel"
                          id="telefone"
                          name="telefone"
                          value={formData.telefone}
                          onChange={handleChange}
                          className={inputClasses('telefone')}
                          placeholder="(11) 99999-9999"
                          data-testid="input-telefone"
                        />
                        {errors.telefone && <p className="text-red-500 text-sm mt-1">{errors.telefone}</p>}
                      </div>

                      {/* Assunto */}
                      <div>
                        <label htmlFor="assunto" className="block text-sm font-semibold text-[#1a3a52] mb-2">
                          Assunto *
                        </label>
                        <input
                          type="text"
                          id="assunto"
                          name="assunto"
                          value={formData.assunto}
                          onChange={handleChange}
                          className={inputClasses('assunto')}
                          placeholder="Ex: Consultoria ISO 27001, Demonstração SmartAudit..."
                          data-testid="input-assunto"
                        />
                        {errors.assunto && <p className="text-red-500 text-sm mt-1">{errors.assunto}</p>}
                      </div>

                      {/* Mensagem */}
                      <div>
                        <label htmlFor="mensagem" className="block text-sm font-semibold text-[#1a3a52] mb-2">
                          Mensagem *
                        </label>
                        <textarea
                          id="mensagem"
                          name="mensagem"
                          rows="5"
                          value={formData.mensagem}
                          onChange={handleChange}
                          className={inputClasses('mensagem')}
                          placeholder="Descreva como podemos ajudar sua organização..."
                          data-testid="input-mensagem"
                        ></textarea>
                        {errors.mensagem && <p className="text-red-500 text-sm mt-1">{errors.mensagem}</p>}
                      </div>

                      {/* Submit Button */}
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-[#10b981] text-white py-4 rounded-xl font-semibold text-lg hover:bg-[#0ea573] transition-all duration-300 hover:shadow-lg hover:shadow-[#10b981]/25 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        data-testid="submit-btn"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Enviando...
                          </>
                        ) : (
                          <>
                            Enviar Mensagem
                            <Send className="w-5 h-5" />
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </motion.div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ContactPage;
